/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2022, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import type { Action, DataType, DialogNode, Intent, Variable } from './action';
import type { Entity, TestingStatusSlice } from './state';
import type { Role } from '.';

export const enum Language {
  Arabic = 'ar',
  ChineseSimplified = 'zh-cn',
  ChineseTraditional = 'zh-tw',
  Czech = 'cs',
  Dutch = 'nl',
  English = 'en',
  French = 'fr',
  German = 'de',
  Italian = 'it',
  Japanese = 'ja',
  Korean = 'ko',
  PortugueseBrazilian = 'pt-br',
  Spanish = 'es',
  Universal = 'xx'
}

export const enum SkillStatus {
  AVAILABLE = 'Available',
  FAILED = 'Failed',
  PROCESSING = 'Processing'
}

export type ActionsWorkspace = {
  actions: Action[];
  entities: Entity[];
  intents: Intent[];
  data_types: DataType[];
  variables: Variable[];
  system_settings: DialogSystemSettings;
  dialog_nodes?: [];
};

export type DialogWorkspace = {
  intents: Intent[];
  entities: Entity[];
  system_settings: DialogSystemSettings;
  dialog_nodes: (DialogNode | any)[];
};

export type Skill = {
  name: string;
  type: string;
  valid: boolean;
  counts: {
    node: number;
    entity: number;
    intent: number;
    intents: number;
    entities: number;
    dialog_nodes: number;
  };
  status: SkillStatus;
  created: string;
  updated: string;
  language: Language;
  skill_id: string;
  description: string;
  assistant_id: string;
  workspace_id: string;
  environment_id?: string;
  dialog_settings?: {
    actions: boolean;
  };
  search_settings?: SearchSettings;
  snapshot_references: SnapshotReference[];
  workspace_reference: WorkspaceReference;
  assistant_references: AssistantReference[];
  next_snapshot_version: string;
  status_errors?: { message: string }[];
  workspace?: ActionsWorkspace | DialogWorkspace;
  role: Role;
  custom_roles: string[];
};

export type CreateSkill = Partial<Skill> & { sample: boolean };

export type SearchSettings = {
  messages: SearchSettingMessages;
  discovery?: SearchSettingsDiscovery;
  milvus?: SearchSettingsMilvus;
  elastic_search?: SearchSettingsElastic;
  schema_mapping: SearchSettingSchemaMapping;
  conversational_search: SearchSettingsConversationalSearch;
  client_side_search?: SearchSettingsClientSide;
  server_side_search?: SearchSettingsServerSide;
};

export type SearchSettingMessages = {
  error: string;
  success: string;
  no_result: string;
};

export type SearchSettingsMilvus = {
  url: string;
  port: string;
  username?: string;
  password?: string;
  database?: string;
  index?: string;
  embeddings_model_id?: string;
  collection?: string;
  filter?: string;
  no_auth?: boolean;
};
export type SearchSettingsElastic = {
  index: string;
  url: string;
  port: string;
  username?: string;
  password?: string;
  apikey?: string;
  filter?: object[];
  query_body?: object;
  managed_index: string;
};

export type SearchSettingsDiscovery = {
  confidence_threshold: number;
  url: string;
  passages: boolean;
  highlight: boolean;
  project_id: string;
  instance_id: string;
  find_answers: boolean;
  results_count: number;
  max_total_results: number;
  max_primary_results: number;
  filter?: object[];
};

export type SearchSettingSchemaMapping = {
  url: string;
  body: string;
  title: string;
};

export type SearchSettingsConversationalSearch = {
  enabled: boolean;
  citations_title?: string;
  search_confidence?: {
    threshold?: string;
    pre_gen_threshold?: string;
    post_gen_threshold?: string;
  };
  response_length?: {
    option?: string;
  };
  multi_turn?: {
    enabled?: boolean;
  };
  citations?: {
    max_citations?: number;
  };
};

export type SearchSettingsClientSide = {
  filter?: string;
  metadata?: object;
};

export type SearchSettingsServerSide = {
  url?: string;
  username?: string;
  password?: string;
  apikey?: string;
  filter?: string;
  metadata?: object;
  no_auth?: boolean;
  auth_type?: CUSTOM_SERVICE_SEARCH_AUTH_TYPES;
};

export enum SEARCH_PROVIDER_TYPES {
  SERVER_SIDE = 'By providing credentials',
  CLIENT_SIDE = 'Through your client'
}

export enum SEARCH_INTEGRATION_TYPES {
  ELASTIC = 'elastic',
  CUSTOM_SERVICE = 'customService',
  MILVUS = 'milvus'
}

export enum CUSTOM_SERVICE_SEARCH_AUTH_TYPES {
  API = 'apikey',
  BASIC = 'basic',
  NONE = 'none'
}

export enum SEARCH_AUTH_TYPES {
  API = 'apikey',
  BASIC = 'basic',
  NONE = 'none'
}

export enum ELASTIC_SEARCH_AUTH_TYPES {
  API = 'API key',
  BASIC = 'Basic authentication'
}

export enum ELASTIC_SEARCH_INDEX_INPUT_METHODS {
  INDEX = 'index',
  UPLOAD = 'upload'
}

export type AssistantReference = {
  name: string;
  assistant_id: string;
  skill_reference: string;
};

export type SnapshotReference = {
  snapshot: string;
  description?: string;
};

export type WorkspaceReference = {
  metadata: {
    skill: {
      counts: {
        intents: number;
        entities: number;
        dialog_nodes: number;
      };
    };
    api_version: {
      major_version: string;
      minor_version: string;
    };
  };
  system_settings: DialogSystemSettings;
  learning_opt_out: boolean;
  status: string;
};

type ConcatValues = {
  scalar?: string;
  skill_variable?: string;
};

type OutputGenericValues = {
  text_expression: {
    concat: ConcatValues[];
  };
};

type OutputGeneric = {
  values?: OutputGenericValues[];
  response_type: string;
  selection_policy?: string;
  options?: any;
  repeat_on_reprompt?: boolean;
};

export type TopicSwitch = {
  digression_handler?: SkillReferenceType.DIALOG | SkillReferenceType.ACTION;
  enabled: boolean;
  messages?: {
    enable_confirmation?: boolean;
    confirmation_request?: {
      generic: OutputGeneric[];
    };
    confirmation_failure?: {
      generic: OutputGeneric[];
    };
  };
  max_tries: number;
};

export type DialogSystemSettings = Partial<{
  auto_learn: {
    apply: boolean;
  };
  disambiguation: Disambiguation;
  variable: Format;
  default_locale?: string;
  launch_mode: LaunchMode;
  generative_ai: GenerativeAI;
  human_agent_assist: {
    prompt: string;
  };
  off_topic: {
    enabled: boolean;
  };
  topic_switch: TopicSwitch;
  spelling_auto_correct: boolean;
  nlp: {
    model: string;
    versions: {
      active: string;
      previous: string;
      latest: string;
      beta: string;
    };
  };
  search_routing: {
    target: string;
  };
}>;

export type Format = {
  format: {
    date: {
      pattern: string;
    };
    time: {
      pattern: string;
    };
    number: {
      delimiter: string;
      fraction_digits: number;
    };
    percent: {
      fraction_digits: number;
      symbol: string;
    };
    currency: {
      fraction_digits: number;
      symbol: string;
    };
  };
};

export type Disambiguation = {
  prompt: string;
  enabled: boolean;
  randomize: boolean;
  max_suggestions: number;
  suggestion_text_policy: string;
  none_of_the_above_prompt: string;
  use_connect_to_support_prompt?: string;
  single_answer_clarification_prompt?: string;
};

export enum LaunchModeResponseMode {
  LEARNING = 'learning',
  CONFIDENT = 'confident'
}

export type LaunchModeParameters = {
  single_answer_clarification: LaunchModeFrequencies;
  disambiguation_clarification: LaunchModeFrequencies;
  use_connect_to_support: LaunchModeFrequencies;
  step_retry_threshold: number;
};

export enum LaunchModeFrequencies {
  RARELY = 'rarely',
  MORE_OFTEN = 'more_often',
  SOMETIMES = 'sometimes',
  LESS_OFTEN = 'less_often'
}

export type LaunchMode = {
  default: LaunchModeResponseMode;
  enabled: boolean;
  learning: LaunchModeParameters;
  confident: LaunchModeParameters;
  no_action_matches?: LaunchModeFrequencies;
};

export type GenerativeAI = {
  general_purpose_answering?: {
    enabled: boolean;
  };
  hap_filter?: {
    output: {
      enabled: boolean;
    };
  };
  model_id?: string;
  prompt_instruction?: string;
  slot_filling: {
    enabled: boolean;
  };
};

export type WorkspaceStatus = {
  skill_id: string;
  training: boolean;
  updated: string;
  workspace_id: string;
  test_status: TestingStatusSlice;
};

export const enum SkillType {
  DIALOG = 'dialog',
  ACTION = 'action',
  SEARCH = 'search'
}

export const enum SkillReferenceType {
  ACTION = 'actions skill',
  DIALOG = 'main skill',
  SEARCH = 'search skill'
}

export const enum ContextVariableType {
  ACTION_VARIABLES = 'action_variables'
}

export const enum ContentChangeType {
  DRAFT = 'draft',
  ENVIRONMENT = 'environment',
  LIVE = 'live',
  ACTIONS = 'Actions',
  ACTIONS_SETTINGS = 'Actions Settings',
  DIALOG_SETTINGS = 'Dialog Settings',
  VARIABLES = 'Variables',
  SETTINGS = 'Settings',
  INTENTS = 'Intents',
  ENTITIES = 'Entities',
  DIALOGS = 'Dialog',
  DIALOG_SKILL = 'Dialog skill',
  COLLECTIONS = 'Collections'
}

export type ContentChange = {
  count?: number;
  isDirty: boolean;
  isNew: boolean;
  isDelete?: boolean;
  lastPublished: string;
  lastUpdated: string;
  name: string;
  type: ContentChangeType;
};

export type MessageContextType = {
  global?: {
    session_id: string;
    system: {
      session_start_time: string;
      state: string;
      turn_count: number;
      user_id: string;
    };
  };
  skills: {
    [SkillReferenceType.DIALOG]: {
      system: {
        state: string;
      };
      user_defined: any;
    };
    [SkillReferenceType.ACTION]?: {};
  };
};

export enum SearchRoutingTarget {
  SEARCH = 'conversational_search',
  NO_MATCHES = 'no_matches'
}

export type SearchRoutingType = {
  search_routing: {
    target: SearchRoutingTarget;
  };
};

export enum MultiTurnType {
  MULTI_TURN = 'multi_turn',
  SINGLE_TURN = 'single_turn'
}

export enum SearchEngineType {
  ELASTICSEARCH = 'Elasticsearch',
  WATSON_DISCOVERY = 'Watson Discovery'
}
